import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { 
  TrendingUp, 
  Users, 
  Globe, 
  Target,
  DollarSign,
  BarChart3,
  ArrowRight,
  CheckCircle,
  Star,
  Shield,
  Lightbulb,
  Calendar,
  Clock,
  MessageCircle,
  Send,
  AlertCircle,
  Phone,
  Mail,
  MapPin,
  Award
} from 'lucide-react'

const Investment: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview')
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    investmentType: '',
    message: ''
  })
  const [projectRecommendationForm, setProjectRecommendationForm] = useState({
    projectName: '',
    description: '',
    category: '',
    stage: '',
    fundingNeeded: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    additionalInfo: ''
  })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const investmentAreas = [
    {
      icon: TrendingUp,
      title: 'Web3项目投资',
      description: '投资具有创新性和成长潜力的Web3项目',
      features: ['DeFi协议', 'NFT平台', 'GameFi项目', 'DAO治理'],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Shield,
      title: '区块链技术投资',
      description: '投资区块链底层技术和基础设施',
      features: ['Layer2解决方案', '跨链技术', '隐私计算', '零知识证明'],
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Target,
      title: '硬件设备投资',
      description: '投资Web3硬件设备和相关技术',
      features: ['硬件钱包', '挖矿设备', '物联网设备', '边缘计算'],
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Lightbulb,
      title: '创新应用投资',
      description: '投资基于Web3的创新应用场景',
      features: ['社交应用', '内容创作', '教育培训', '医疗健康'],
      color: 'from-orange-500 to-red-500'
    }
  ]

  const fundingRounds = [
    {
      round: '种子轮',
      amount: '$2M',
      date: '2023 Q1',
      investors: ['ABC Capital', 'XYZ Ventures'],
      status: '已完成'
    },
    {
      round: 'A轮',
      amount: '$10M',
      date: '2023 Q3',
      investors: ['DEF Fund', 'GHI Partners'],
      status: '已完成'
    },
    {
      round: 'B轮',
      amount: '$25M',
      date: '2024 Q2',
      investors: ['JKL Capital', 'MNO Ventures'],
      status: '进行中'
    }
  ]

  const partners = [
    { name: 'ABC Capital', logo: 'ABC', description: '专注于区块链技术投资' },
    { name: 'XYZ Ventures', logo: 'XYZ', description: 'Web3生态建设者' },
    { name: 'DEF Fund', logo: 'DEF', description: '创新技术投资机构' },
    { name: 'GHI Partners', logo: 'GHI', description: '硬件设备投资专家' },
    { name: 'JKL Capital', logo: 'JKL', description: '全球投资管理' },
    { name: 'MNO Ventures', logo: 'MNO', description: '科技创业投资' }
  ]

  const projectProgress = [
    {
      id: 1,
      name: 'CoreTrust手机研发',
      status: '进行中',
      progress: 75,
      startDate: '2023-01-15',
      expectedEnd: '2024-06-30',
      budget: '$15M',
      spent: '$11.2M',
      team: 45,
      milestones: [
        { name: '硬件设计完成', status: 'completed', date: '2023-03-15' },
        { name: '原型机测试', status: 'completed', date: '2023-08-20' },
        { name: '软件系统开发', status: 'in-progress', date: '2024-02-15' },
        { name: '量产准备', status: 'pending', date: '2024-05-01' }
      ]
    },
    {
      id: 2,
      name: 'Web3生态建设',
      status: '进行中',
      progress: 60,
      startDate: '2023-06-01',
      expectedEnd: '2024-12-31',
      budget: '$8M',
      spent: '$4.8M',
      team: 28,
      milestones: [
        { name: 'DApp市场开发', status: 'completed', date: '2023-09-30' },
        { name: '钱包集成', status: 'completed', date: '2023-12-15' },
        { name: 'DeFi协议集成', status: 'in-progress', date: '2024-03-01' },
        { name: 'NFT市场上线', status: 'pending', date: '2024-06-01' }
      ]
    },
    {
      id: 3,
      name: '全球市场拓展',
      status: '规划中',
      progress: 25,
      startDate: '2024-03-01',
      expectedEnd: '2025-03-31',
      budget: '$12M',
      spent: '$3M',
      team: 35,
      milestones: [
        { name: '市场调研完成', status: 'completed', date: '2024-01-15' },
        { name: '合作伙伴签约', status: 'in-progress', date: '2024-04-01' },
        { name: '产品本地化', status: 'pending', date: '2024-07-01' },
        { name: '全球发布', status: 'pending', date: '2024-10-01' }
      ]
    }
  ]

  const investmentTypes = [
    'Web3项目投资',
    '区块链技术投资',
    '硬件设备投资',
    '创新应用投资',
    '生态建设投资',
    '其他'
  ]

  const projectCategories = [
    'DeFi协议',
    'NFT平台',
    'GameFi项目',
    'DAO治理',
    'Layer2解决方案',
    '跨链技术',
    '隐私计算',
    '零知识证明',
    '硬件钱包',
    '挖矿设备',
    '物联网设备',
    '边缘计算',
    '社交应用',
    '内容创作',
    '教育培训',
    '医疗健康',
    '其他'
  ]

  const projectStages = [
    '概念阶段',
    'MVP开发',
    '测试网阶段',
    '主网上线',
    '成长期',
    '成熟期'
  ]

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setConsultationForm(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleProjectRecommendationChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setProjectRecommendationForm(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleConsultationSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    try {
      // 这里应该调用API提交投资咨询
      // await apiService.post('/investment/consultation', consultationForm)
      
      // 模拟提交成功
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      setMessage('投资咨询提交成功！我们会尽快与您联系。')
      setConsultationForm({
        name: '',
        company: '',
        email: '',
        phone: '',
        investmentType: '',
        message: ''
      })
    } catch (err: any) {
      setError(err.message || '提交失败，请重试')
    } finally {
      setLoading(false)
    }
  }

  const handleProjectRecommendationSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    try {
      // 这里应该调用API提交项目推荐
      // await apiService.post('/investment/project-recommendation', projectRecommendationForm)
      
      // 模拟提交成功
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      setMessage('项目推荐提交成功！我们会尽快评估并与您联系。')
      setProjectRecommendationForm({
        projectName: '',
        description: '',
        category: '',
        stage: '',
        fundingNeeded: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        website: '',
        additionalInfo: ''
      })
    } catch (err: any) {
      setError(err.message || '提交失败，请重试')
    } finally {
      setLoading(false)
    }
  }

  const metrics = [
    { icon: DollarSign, value: '$37M', label: '累计融资' },
    { icon: Users, value: '50+', label: '投资机构' },
    { icon: Globe, value: '20+', label: '投资项目' },
    { icon: BarChart3, value: '300%', label: '平均回报率' }
  ]

  return (
    <>
      <Helmet>
        <title>投资合作 - DeepLink Limited</title>
        <meta name="description" content="DeepLink Limited投资合作信息，包括投资方向、融资进展、合作伙伴等。" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-green-900 via-blue-900 to-purple-900">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                投资合作
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
                携手共建Web3生态，共创数字未来
              </p>
            </motion.div>
          </div>
        </section>

        {/* Tab Navigation */}
        <section className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center space-x-1 py-4">
              {[
                { id: 'overview', label: '投资概览', icon: BarChart3 },
                { id: 'progress', label: '项目推进', icon: TrendingUp },
                { id: 'consultation', label: '投资咨询', icon: MessageCircle },
                { id: 'recommendation', label: '项目推荐', icon: Star }
              ].map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {tab.label}
                  </button>
                )
              })}
            </div>
          </div>
        </section>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <>
            {/* Metrics Section */}
            <section className="py-20 bg-white dark:bg-gray-800">
              <div className="container mx-auto px-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                  {metrics.map((metric, index) => {
                    const Icon = metric.icon
                    return (
                      <motion.div
                        key={metric.label}
                        initial={{ opacity: 0, scale: 0.5 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        className="text-center"
                      >
                        <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <div className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                          {metric.value}
                        </div>
                        <div className="text-lg text-gray-600 dark:text-gray-400">
                          {metric.label}
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              </div>
            </section>

            {/* Investment Areas */}
            <section className="py-20">
              <div className="container mx-auto px-4">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className="text-center mb-16"
                >
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                    投资方向
                  </h2>
                  <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                    我们专注于Web3生态系统的各个领域，寻找具有创新性和成长潜力的项目
                  </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {investmentAreas.map((area, index) => {
                    const Icon = area.icon
                    return (
                      <motion.div
                        key={area.title}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        className="group bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                      >
                        <div className={`w-16 h-16 bg-gradient-to-r ${area.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                          {area.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                          {area.description}
                        </p>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                            重点关注：
                          </h4>
                          <ul className="space-y-2">
                            {area.features.map((feature, idx) => (
                              <li key={idx} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              </div>
            </section>

            {/* Funding Rounds */}
            <section className="py-20 bg-gray-50 dark:bg-gray-900">
              <div className="container mx-auto px-4">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className="text-center mb-16"
                >
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                    融资进展
                  </h2>
                  <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                    我们已完成多轮融资，获得了众多知名投资机构的支持
                  </p>
                </motion.div>

                <div className="space-y-8">
                  {fundingRounds.map((round, index) => (
                    <motion.div
                      key={round.round}
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg"
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                        <div className="mb-4 md:mb-0">
                          <div className="flex items-center space-x-4 mb-2">
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                              {round.round}
                            </h3>
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                              round.status === '已完成' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            }`}>
                              {round.status}
                            </span>
                          </div>
                          <p className="text-gray-600 dark:text-gray-400">
                            {round.date}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                            {round.amount}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            融资金额
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                          投资机构：
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {round.investors.map((investor, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                            >
                              {investor}
                            </span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>

            {/* Partners */}
            <section className="py-20 bg-white dark:bg-gray-800">
              <div className="container mx-auto px-4">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className="text-center mb-16"
                >
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                    合作伙伴
                  </h2>
                  <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                    感谢所有合作伙伴的支持，共同推动Web3生态发展
                  </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {partners.map((partner, index) => (
                    <motion.div
                      key={partner.name}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow duration-300"
                    >
                      <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-white font-bold text-lg">
                          {partner.logo}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                        {partner.name}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        {partner.description}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-green-600 to-blue-600">
              <div className="container mx-auto px-4 text-center">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                    加入我们的投资生态
                  </h2>
                  <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
                    如果您是投资机构或对Web3项目感兴趣，欢迎与我们联系
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button 
                      onClick={() => setActiveTab('consultation')}
                      className="bg-white text-green-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors duration-300 flex items-center justify-center space-x-2"
                    >
                      <span>投资咨询</span>
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => setActiveTab('recommendation')}
                      className="bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/20 transition-colors duration-300"
                    >
                      项目推荐
                    </button>
                  </div>
                </motion.div>
              </div>
            </section>
          </>
        )}

        {/* Project Progress Tab */}
        {activeTab === 'progress' && (
          <div className="py-20 bg-gray-50 dark:bg-gray-900">
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  项目推进跟踪
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                  实时跟踪我们的核心项目进展，了解投资回报和里程碑达成情况
                </p>
              </motion.div>

              <div className="space-y-8">
                {projectProgress.map((project, index) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                      <div className="mb-4 lg:mb-0">
                        <div className="flex items-center space-x-4 mb-2">
                          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                            {project.name}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            project.status === '进行中' 
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                              : project.status === '规划中'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          }`}>
                            {project.status}
                          </span>
                        </div>
                        <div className="flex items-center space-x-6 text-sm text-gray-600 dark:text-gray-400">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            开始：{project.startDate}
                          </div>
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            预计完成：{project.expectedEnd}
                          </div>
                          <div className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            团队：{project.team}人
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                          {project.progress}%
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          完成进度
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-6">
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-1000"
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Budget and Spending */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                      <div className="text-center p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <div className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                          {project.budget}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          总预算
                        </div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <div className="text-lg font-bold text-green-600 dark:text-green-400 mb-1">
                          {project.spent}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          已花费
                        </div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <div className="text-lg font-bold text-blue-600 dark:text-blue-400 mb-1">
                          {Math.round((parseFloat(project.spent.replace('$', '').replace('M', '')) / parseFloat(project.budget.replace('$', '').replace('M', '')) * 100))}%
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          预算使用率
                        </div>
                      </div>
                    </div>

                    {/* Milestones */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        项目里程碑
                      </h4>
                      <div className="space-y-3">
                        {project.milestones.map((milestone, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                            <div className="flex items-center">
                              <div className={`w-3 h-3 rounded-full mr-3 ${
                                milestone.status === 'completed' 
                                  ? 'bg-green-500'
                                  : milestone.status === 'in-progress'
                                  ? 'bg-blue-500'
                                  : 'bg-gray-300 dark:bg-gray-600'
                              }`}></div>
                              <span className="text-gray-900 dark:text-white font-medium">
                                {milestone.name}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {milestone.date}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Investment Consultation Tab */}
        {activeTab === 'consultation' && (
          <div className="py-20 bg-gray-50 dark:bg-gray-900">
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  投资咨询
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                  专业的投资咨询服务，为您的投资决策提供全方位的支持
                </p>
              </motion.div>

              <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Consultation Form */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                      提交咨询
                    </h3>
                    
                    {message && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center"
                      >
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                        <span className="text-green-700 dark:text-green-400">{message}</span>
                      </motion.div>
                    )}

                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center"
                      >
                        <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                        <span className="text-red-700 dark:text-red-400">{error}</span>
                      </motion.div>
                    )}

                    <form onSubmit={handleConsultationSubmit} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          姓名 *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={consultationForm.name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="请输入您的姓名"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          公司/机构
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={consultationForm.company}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="请输入公司或机构名称"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          邮箱 *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={consultationForm.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="请输入您的邮箱"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          电话
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={consultationForm.phone}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="请输入您的电话"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          投资类型
                        </label>
                        <select
                          name="investmentType"
                          value={consultationForm.investmentType}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="">请选择投资类型</option>
                          {investmentTypes.map((type, index) => (
                            <option key={index} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          咨询内容
                        </label>
                        <textarea
                          name="message"
                          value={consultationForm.message}
                          onChange={handleInputChange}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="请详细描述您的投资需求或咨询问题..."
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {loading ? (
                          <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        {loading ? '提交中...' : '提交咨询'}
                      </button>
                    </form>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-6">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                        联系方式
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center">
                          <Mail className="w-5 h-5 text-blue-600 mr-3" />
                          <span className="text-gray-700 dark:text-gray-300">investment@deeplink.com</span>
                        </div>
                        <div className="flex items-center">
                          <Phone className="w-5 h-5 text-blue-600 mr-3" />
                          <span className="text-gray-700 dark:text-gray-300">+86 400-888-8888</span>
                        </div>
                        <div className="flex items-center">
                          <MapPin className="w-5 h-5 text-blue-600 mr-3" />
                          <span className="text-gray-700 dark:text-gray-300">北京市朝阳区科技园区</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                        服务优势
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <Award className="w-5 h-5 text-green-600 mr-3 mt-1" />
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">专业团队</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">资深投资专家团队，丰富的Web3投资经验</p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <TrendingUp className="w-5 h-5 text-green-600 mr-3 mt-1" />
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">高回报率</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">平均投资回报率超过300%</p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <Shield className="w-5 h-5 text-green-600 mr-3 mt-1" />
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">风险控制</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">完善的风险评估和控制体系</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Project Recommendation Tab */}
        {activeTab === 'recommendation' && (
          <div className="py-20 bg-gray-50 dark:bg-gray-900">
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  项目推荐
                </h2>
                <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                  推荐优秀的Web3项目，与我们共同发现下一个独角兽
                </p>
              </motion.div>

              <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Project Recommendation Form */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                      推荐项目
                    </h3>
                    
                    {message && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center"
                      >
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                        <span className="text-green-700 dark:text-green-400">{message}</span>
                      </motion.div>
                    )}

                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center"
                      >
                        <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                        <span className="text-red-700 dark:text-red-400">{error}</span>
                      </motion.div>
                    )}

                    <form onSubmit={handleProjectRecommendationSubmit} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          项目名称 *
                        </label>
                        <input
                          type="text"
                          name="projectName"
                          value={projectRecommendationForm.projectName}
                          onChange={handleProjectRecommendationChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="请输入项目名称"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          项目描述 *
                        </label>
                        <textarea
                          name="description"
                          value={projectRecommendationForm.description}
                          onChange={handleProjectRecommendationChange}
                          required
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="请详细描述项目的核心功能、技术特点、商业模式等..."
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            项目类别 *
                          </label>
                          <select
                            name="category"
                            value={projectRecommendationForm.category}
                            onChange={handleProjectRecommendationChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="">请选择项目类别</option>
                            {projectCategories.map((category, index) => (
                              <option key={index} value={category}>{category}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            项目阶段 *
                          </label>
                          <select
                            name="stage"
                            value={projectRecommendationForm.stage}
                            onChange={handleProjectRecommendationChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="">请选择项目阶段</option>
                            {projectStages.map((stage, index) => (
                              <option key={index} value={stage}>{stage}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          融资需求
                        </label>
                        <input
                          type="text"
                          name="fundingNeeded"
                          value={projectRecommendationForm.fundingNeeded}
                          onChange={handleProjectRecommendationChange}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="请输入融资需求金额"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          项目网站
                        </label>
                        <input
                          type="url"
                          name="website"
                          value={projectRecommendationForm.website}
                          onChange={handleProjectRecommendationChange}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="请输入项目官网或相关链接"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          推荐人姓名 *
                        </label>
                        <input
                          type="text"
                          name="contactName"
                          value={projectRecommendationForm.contactName}
                          onChange={handleProjectRecommendationChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="请输入您的姓名"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            推荐人邮箱 *
                          </label>
                          <input
                            type="email"
                            name="contactEmail"
                            value={projectRecommendationForm.contactEmail}
                            onChange={handleProjectRecommendationChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="请输入您的邮箱"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            推荐人电话
                          </label>
                          <input
                            type="tel"
                            name="contactPhone"
                            value={projectRecommendationForm.contactPhone}
                            onChange={handleProjectRecommendationChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="请输入您的电话"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          补充信息
                        </label>
                        <textarea
                          name="additionalInfo"
                          value={projectRecommendationForm.additionalInfo}
                          onChange={handleProjectRecommendationChange}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="其他需要说明的信息..."
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg hover:from-green-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                      >
                        {loading ? (
                          <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        ) : (
                          <Star className="w-4 h-4 mr-2" />
                        )}
                        {loading ? '提交中...' : '推荐项目'}
                      </button>
                    </form>
                  </div>

                  {/* Recommendation Guidelines */}
                  <div className="space-y-6">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                        推荐指南
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-1" />
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">项目要求</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">项目必须与Web3、区块链或相关技术相关</p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-1" />
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">创新性</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">项目应具有技术创新或商业模式创新</p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-1" />
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">团队实力</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">团队应具备相关技术背景和执行能力</p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-1" />
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">市场潜力</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">项目应具有明确的市场需求和商业价值</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                        推荐奖励
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center">
                          <Award className="w-5 h-5 text-yellow-500 mr-3" />
                          <span className="text-gray-700 dark:text-gray-300">成功推荐项目可获得推荐奖励</span>
                        </div>
                        <div className="flex items-center">
                          <TrendingUp className="w-5 h-5 text-blue-500 mr-3" />
                          <span className="text-gray-700 dark:text-gray-300">优先获得项目投资机会</span>
                        </div>
                        <div className="flex items-center">
                          <Users className="w-5 h-5 text-purple-500 mr-3" />
                          <span className="text-gray-700 dark:text-gray-300">加入我们的推荐人网络</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                        联系方式
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center">
                          <Mail className="w-5 h-5 text-blue-600 mr-3" />
                          <span className="text-gray-700 dark:text-gray-300">projects@deeplink.com</span>
                        </div>
                        <div className="flex items-center">
                          <Phone className="w-5 h-5 text-blue-600 mr-3" />
                          <span className="text-gray-700 dark:text-gray-300">+86 400-888-8888</span>
                        </div>
                        <div className="flex items-center">
                          <MapPin className="w-5 h-5 text-blue-600 mr-3" />
                          <span className="text-gray-700 dark:text-gray-300">北京市朝阳区科技园区</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default Investment